import React, { useState, useMemo } from 'react';
import { 
    Box, 
    Typography, 
    Grid, 
    Paper, 
    Chip, 
    Divider,
    Card,
    CardContent,
    useTheme,
    IconButton,
    Tooltip
} from '@mui/material';
import { SandboxConsentDetails } from '../../../services/sandbox-consents-service';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningIcon from '@mui/icons-material/Warning';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NumbersIcon from '@mui/icons-material/Numbers';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';

interface DataElementsTabProps {
    consentData: SandboxConsentDetails;
}

const DataElementsTab: React.FC<DataElementsTabProps> = ({ consentData }) => {
    const theme = useTheme();
    const { dataElements } = consentData;
    

    // Get data type icon
    const getDataTypeIcon = (types: string[]) => {
        const typeString = types.join(' ').toLowerCase();
        if (typeString.includes('email')) return <EmailIcon sx={{ fontSize: 16 }} />;
        if (typeString.includes('phone')) return <PhoneIcon sx={{ fontSize: 16 }} />;
        if (typeString.includes('date')) return <CalendarTodayIcon sx={{ fontSize: 16 }} />;
        if (typeString.includes('number') || typeString.includes('numeric')) return <NumbersIcon sx={{ fontSize: 16 }} />;
        if (typeString.includes('boolean') || typeString.includes('checkbox')) return <ToggleOnIcon sx={{ fontSize: 16 }} />;
        return <TextFieldsIcon sx={{ fontSize: 16 }} />;
    };

    // Get data type color
    const getDataTypeColor = (types: string[]): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default' => {
        const typeString = types.join(' ').toLowerCase();
        if (typeString.includes('email')) return 'primary';
        if (typeString.includes('phone')) return 'success';
        if (typeString.includes('date')) return 'secondary';
        if (typeString.includes('number') || typeString.includes('numeric')) return 'warning';
        if (typeString.includes('boolean') || typeString.includes('checkbox')) return 'info';
        return 'default';
    };

    // Format value for display
    const formatValue = (value: any, types: string[]): string => {
        if (value === null || value === undefined || value === '') {
            return 'Not provided';
        }
        
        const typeString = types.join(' ').toLowerCase();
        
        if (typeString.includes('date')) {
            try {
                return new Date(value).toLocaleDateString();
            } catch {
                return String(value);
            }
        }
        
        if (typeString.includes('boolean')) {
            return value ? 'Yes' : 'No';
        }
        
        return String(value);
    };

    // Check if value is provided
    const isValueProvided = (value: any): boolean => {
        return value !== null && value !== undefined && value !== '';
    };


    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        const total = dataElements.length;
        const required = dataElements.filter(e => e.isRequired).length;
        const personalData = dataElements.filter(e => e.isPersonalData).length;
        const provided = dataElements.filter(e => isValueProvided(e.value)).length;
        
        return { total, required, personalData, provided };
    }, [dataElements]);


    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <DescriptionIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h5" component="h2" gutterBottom>
                            Data Elements
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Personal data collected during consent submission
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Summary Statistics Cards */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: 'auto' }}>
                        <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2 }}>
                            <DescriptionIcon sx={{ fontSize: 24, color: 'primary.main', mb: 0.5 }} />
                            <Typography variant="h5" component="div" color="primary">
                                {summaryStats.total}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Total Elements
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: 'auto' }}>
                        <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2 }}>
                            <WarningIcon sx={{ fontSize: 24, color: 'warning.main', mb: 0.5 }} />
                            <Typography variant="h5" component="div" color="warning.main">
                                {summaryStats.required}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Required Elements
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: 'auto' }}>
                        <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2 }}>
                            <SecurityIcon sx={{ fontSize: 24, color: 'info.main', mb: 0.5 }} />
                            <Typography variant="h5" component="div" color="info.main">
                                {summaryStats.personalData}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Personal Data
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: 'auto' }}>
                        <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2 }}>
                            <CheckCircleIcon sx={{ fontSize: 24, color: 'success.main', mb: 0.5 }} />
                            <Typography variant="h5" component="div" color="success.main">
                                {summaryStats.provided}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Values Provided
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>


            {/* Data Elements List */}
            <Box>
                <Typography variant="h6" gutterBottom>
                    Data Elements ({dataElements.length})
                </Typography>
                
                {dataElements.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No data elements found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            No data elements were collected for this consent
                        </Typography>
                    </Paper>
                ) : (
                    <Grid container spacing={2}>
                        {dataElements.map((element) => (
                            <Grid item xs={12} key={element.id}>
                                <Paper sx={{ p: 3 }}>
                                    <Grid container spacing={3} alignItems="flex-start">
                                        {/* Left Section - Element Info */}
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                {getDataTypeIcon(element.type)}
                                                <Typography variant="h6" component="h3">
                                                    {element.name}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Key
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                                                    {element.key}
                                                </Typography>
                                            </Box>
                                            
                                            {element.description && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Description
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {element.description}
                                                    </Typography>
                                                </Box>
                                            )}
                                            
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip
                                                    icon={getDataTypeIcon(element.type)}
                                                    label={element.type.join(', ')}
                                                    color={getDataTypeColor(element.type)}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    icon={element.isRequired ? <WarningIcon /> : <CheckCircleIcon />}
                                                    label={element.isRequired ? 'Required' : 'Optional'}
                                                    color={element.isRequired ? 'error' : 'default'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </Grid>
                                        
                                        {/* Right Section - Value & Metadata */}
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Provided Value
                                                </Typography>
                                                <Typography 
                                                    variant="body1" 
                                                    sx={{ 
                                                        fontFamily: isValueProvided(element.value) ? 'inherit' : 'monospace',
                                                        color: isValueProvided(element.value) ? 'text.primary' : 'text.secondary',
                                                        fontStyle: isValueProvided(element.value) ? 'normal' : 'italic'
                                                    }}
                                                >
                                                    {formatValue(element.value, element.type)}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                                <Chip
                                                    icon={element.isPersonalData ? <SecurityIcon /> : <CheckCircleIcon />}
                                                    label={element.isPersonalData ? 'Personal Data' : 'Non-Personal Data'}
                                                    color={element.isPersonalData ? 'info' : 'success'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    icon={isValueProvided(element.value) ? <CheckCircleIcon /> : <CancelIcon />}
                                                    label={isValueProvided(element.value) ? 'Provided' : 'Missing'}
                                                    color={isValueProvided(element.value) ? 'success' : 'error'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                            
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Storage Mode
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <StorageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="body2">
                                                        {element.storageMode || 'Standard'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Box>
    );
};

export default DataElementsTab;
